import React from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiLock, FiEye, FiDatabase, FiMail, FiCheck, FiShare2, FiCalendar, FiTrash2 } from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';

const PrivacyPolicy = () => {
  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: FiShield,
      content: `Tasklina ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered task management platform and services. This policy also describes your choices regarding the collection, use, and disclosure of your personal information.`
    },
    {
      id: 'information-collected',
      title: 'Information We Collect',
      icon: FiDatabase,
      subsections: [
        {
          title: 'Account Information',
          items: [
            'Name and email address when you create an account',
            'Profile picture and display preferences',
            'Authentication credentials (securely hashed)',
            'Organization and team membership details'
          ]
        },
        {
          title: 'Usage Data',
          items: [
            'Tasks, projects, and milestones you create',
            'Files and documents you upload',
            'Collaboration and communication data',
            'Application usage patterns and preferences'
          ]
        },
        {
          title: 'Technical Information',
          items: [
            'Device type and browser information',
            'IP address and approximate location',
            'Cookies and similar tracking technologies'
          ]
        }
      ]
    },
    {
      id: 'google-user-data',
      title: 'Google User Data',
      icon: FiCalendar,
      content: `Tasklina integrates with Google services to provide sign-in authentication and calendar synchronization features. This section describes how we handle data obtained through Google APIs in compliance with the Google API Services User Data Policy, including the Limited Use requirements.`,
      subsections: [
        {
          title: 'Google Sign-In (OAuth Authentication)',
          items: [
            'When you sign in with Google, we access your name, email address, and profile picture from your Google account to create and manage your Tasklina account',
            'We store your Google account identifier (Google ID) to enable seamless sign-in on future visits',
            'We do NOT access your Google password — authentication is handled securely through Google\'s OAuth 2.0 protocol'
          ]
        },
        {
          title: 'Google Calendar Integration',
          items: [
            'Google Calendar: We read and write calendar events to sync your Tasklina tasks and project deadlines with your Google Calendar',
            'Google Account Email: We retrieve your Google account email address solely to display which account is connected in the Tasklina interface',
            'OAuth Tokens: We securely store OAuth access and refresh tokens to maintain your calendar connection without requiring repeated sign-ins'
          ]
        },
        {
          title: 'How We Use Google User Data',
          items: [
            'Authenticate your identity and provide access to your Tasklina account when using Google Sign-In',
            'Create, update, and delete calendar events in your Google Calendar that correspond to your Tasklina tasks and project deadlines',
            'Display your connected Google account email within the Tasklina app for identification purposes',
            'Automatically refresh authentication tokens to maintain a seamless connection'
          ]
        },
        {
          title: 'Google Data Sharing, Transfer & Disclosure',
          items: [
            'We do NOT sell, rent, or trade your Google user data to any third parties',
            'We do NOT share your Google Calendar data with other Tasklina users or any external organizations',
            'We do NOT use your Google user data for advertising, marketing, or profiling purposes',
            'We do NOT transfer your Google user data to any third-party services, tools, or AI/ML models',
            'Tasklina\'s use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements',
            'Your Google user data is only used to provide and improve the features you explicitly authorized (sign-in and/or calendar synchronization)',
            'We may disclose Google user data only if required by law, such as in response to a valid legal process (e.g., subpoena, court order)'
          ]
        },
        {
          title: 'Revoking Google Access',
          items: [
            'You can disconnect your Google Calendar from Tasklina at any time through the calendar settings in the app',
            'Upon disconnection, we delete all stored Google OAuth tokens and remove all Tasklina-created events from your Google Calendar',
            'You can also revoke Tasklina\'s access from your Google Account settings at https://myaccount.google.com/permissions'
          ]
        }
      ]
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      icon: FiEye,
      items: [
        'Provide, maintain, and improve Tasklina services',
        'Process and manage your tasks, projects, and workflows',
        'Enable real-time collaboration features',
        'Synchronize your tasks and deadlines with connected third-party calendars (e.g., Google Calendar)',
        'Send important notifications and service updates',
        'Provide AI-powered insights and recommendations',
        'Ensure platform security and prevent fraud',
        'Comply with legal obligations'
      ]
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing & Disclosure',
      icon: FiShare2,
      content: `We are committed to keeping your data private. We share your personal information only in the following limited circumstances:`,
      subsections: [
        {
          title: 'With Your Consent',
          items: [
            'When you invite team members to collaborate on projects, relevant project and task data is shared with those members',
            'When you connect third-party services (e.g., Google Calendar), data is shared only as necessary to provide the integration you authorized'
          ]
        },
        {
          title: 'Service Providers',
          items: [
            'Cloud hosting providers that store and process data on our behalf (with appropriate data processing agreements)',
            'Email delivery services for sending notifications and verification emails',
            'We do NOT sell, rent, or trade your personal data to any third parties for their own marketing or commercial purposes'
          ]
        },
        {
          title: 'Legal Requirements',
          items: [
            'To comply with applicable laws, regulations, or valid legal processes',
            'To protect the rights, property, or safety of Tasklina, our users, or the public',
            'To enforce our Terms of Service'
          ]
        }
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: FiLock,
      content: `We implement industry-standard security measures to protect your data:`,
      items: [
        'End-to-end encryption for sensitive data',
        'Secure cloud infrastructure with regular security audits',
        'Two-factor authentication support',
        'Regular automated backups',
        'Access controls and monitoring',
        'OAuth tokens are securely stored with encryption and are never exposed to client-side code'
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention & Deletion',
      icon: FiTrash2,
      content: `We retain your personal data only as long as necessary to provide our services or as required by law.`,
      items: [
        'Active account data is retained for the duration of your account',
        'When you delete your account, all personal data including tasks, projects, and associated files are permanently deleted within 30 days',
        'Google Calendar OAuth tokens are deleted immediately when you disconnect your calendar or delete your account',
        'Backups containing your data are automatically purged within 90 days of account deletion',
        'Anonymized usage analytics may be retained for service improvement but cannot be traced back to individual users'
      ]
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      icon: FiCheck,
      items: [
        'Access and download your personal data',
        'Correct inaccurate information',
        'Delete your account and associated data',
        'Export your data in standard formats',
        'Opt-out of marketing communications',
        'Withdraw consent for data processing',
        'Revoke third-party integrations (e.g., Google Calendar) at any time'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-2xl">
              <FiShield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Last updated: February 15, 2026</p>
            </div>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl leading-relaxed">
            Your privacy matters to us. This policy describes how Tasklina collects, uses, and protects your personal information.
          </p>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 border-b border-gray-100 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap gap-3">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <main className="py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-12">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section 
                  key={section.id} 
                  id={section.id}
                  className="scroll-mt-24"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md dark:shadow-gray-900/30 transition-shadow p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                        <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white pt-1">
                        {section.title}
                      </h2>
                    </div>

                    {section.content && (
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 pl-14">
                        {section.content}
                      </p>
                    )}

                    {section.subsections && (
                      <div className="space-y-6 pl-14">
                        {section.subsections.map((sub, idx) => (
                          <div key={idx}>
                            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">
                              {sub.title}
                            </h3>
                            <ul className="space-y-2.5">
                              {sub.items.map((item, itemIdx) => (
                                <li key={itemIdx} className="flex items-start space-x-3">
                                  <FiCheck className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-600 dark:text-gray-300">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {section.items && !section.subsections && (
                      <ul className="space-y-3 pl-14">
                        {section.items.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <FiCheck className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-300">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              );
            })}

            {/* Contact Section */}
            <section className="bg-gradient-to-br from-blue-600 to-blue-700 dark:from-blue-800 dark:to-blue-900 rounded-2xl p-8 text-white">
              <div className="flex items-start space-x-4">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <FiMail className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold mb-3">Questions About Privacy?</h2>
                  <p className="text-blue-100 mb-6 leading-relaxed">
                    If you have any questions about this Privacy Policy or how we handle your data, 
                    please don't hesitate to reach out.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a 
                      href="mailto:privacy@tasklina.com"
                      className="inline-flex items-center px-5 py-2.5 bg-white text-blue-600 rounded-full font-medium hover:bg-blue-50 transition-colors"
                    >
                      <FiMail className="w-4 h-4 mr-2" />
                      privacy@tasklina.com
                    </a>
                    <Link 
                      to="/contact"
                      className="inline-flex items-center px-5 py-2.5 bg-blue-500 text-white rounded-full font-medium hover:bg-blue-400 transition-colors"
                    >
                      Contact Us
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <PublicPageFooter />
    </div>
  );
};

export default PrivacyPolicy;
